import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { MASTERDATA_API_BASE } from "./apiConfig";

const VinereaExclusivaManager = ({ onBack }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processingIds, setProcessingIds] = useState([]);
  const [deletingAll, setDeletingAll] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${MASTERDATA_API_BASE}/vinerea-exclusiva`);
      if (!response.ok) {
        throw new Error(`Eroare la încărcarea produselor (${response.status})`);
      }
      const data = await response.json();
      setProducts(Array.isArray(data?.products) ? data.products : []);
    } catch (err) {
      console.error("Vinerea Exclusivă fetch error", err);
      setError(err.message || "Nu s-au putut încărca produsele.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleDelete = async (productId) => {
    if (!productId) return;
    const confirmDelete = window.confirm(
      "Sigur dorești să ștergi acest produs?"
    );
    if (!confirmDelete) return;

    setProcessingIds((prev) => [...prev, productId]);
    try {
      const response = await fetch(
        `${MASTERDATA_API_BASE}/vinerea-exclusiva/${productId}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) {
        throw new Error(`Ștergerea a eșuat (${response.status})`);
      }
      setProducts((prev) => prev.filter((product) => product.id !== productId));
      setSnackbar({
        open: true,
        severity: "success",
        message: "Produs șters cu succes.",
      });
    } catch (err) {
      console.error("Vinerea Exclusivă delete error", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: err.message || "Ștergerea produsului a eșuat.",
      });
    } finally {
      setProcessingIds((prev) => prev.filter((id) => id !== productId));
    }
  };

  const handleDeleteAll = async () => {
    if (!products.length) return;
    const confirmDelete = window.confirm(
      "Această acțiune va șterge toate produsele din categoria Vinerea Exclusivă. Continui?"
    );
    if (!confirmDelete) return;

    setDeletingAll(true);
    try {
      const response = await fetch(`${MASTERDATA_API_BASE}/vinerea-exclusiva`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error(`Ștergerea produselor a eșuat (${response.status})`);
      }
      setProducts([]);
      setSnackbar({
        open: true,
        severity: "success",
        message: "Toate produsele au fost șterse.",
      });
    } catch (err) {
      console.error("Vinerea Exclusivă delete-all error", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: err.message || "Ștergerea produselor a eșuat.",
      });
    } finally {
      setDeletingAll(false);
    }
  };

  const totalValue = useMemo(() => {
    return products.reduce((acc, product) => {
      const price = Number(product.price || product.regularPrice || 0);
      return acc + (Number.isFinite(price) ? price : 0);
    }, 0);
  }, [products]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
          >
            Înapoi
          </Button>
          <Typography variant="h5" fontWeight={700}>
            Manager Vinerea Exclusivă
          </Typography>
        </Stack>

        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Produse programate pentru Vinerea Exclusivă
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {products.length} produse • Valoare totală estimată:{" "}
                {totalValue.toLocaleString("ro-RO", {
                  style: "currency",
                  currency: "RON",
                  maximumFractionDigits: 0,
                })}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchProducts}
                disabled={loading}
              >
                Reîncarcă
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteAll}
                disabled={deletingAll || !products.length}
              >
                Șterge toate
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Stack>
          ) : products.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <Typography variant="subtitle1" color="text.secondary">
                Nu există produse în categoria Vinerea Exclusivă.
              </Typography>
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Produs</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">Preț</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Acțiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product) => {
                    const isProcessing = processingIds.includes(product.id);
                    return (
                      <TableRow key={product.id} hover>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              dangerouslySetInnerHTML={{ __html: product.name }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ID WooCommerce: {product.id}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {product.sku || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {Number(product.price || product.regularPrice) ? (
                            <Typography variant="body2" fontWeight={600}>
                              {Number(
                                product.price || product.regularPrice
                              ).toLocaleString("ro-RO", {
                                style: "currency",
                                currency: "RON",
                                maximumFractionDigits: 0,
                              })}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={product.status}
                            color={
                              product.status === "publish"
                                ? "success"
                                : "default"
                            }
                            variant={
                              product.status === "publish"
                                ? "filled"
                                : "outlined"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Șterge produsul din Vinerea Exclusivă">
                            <span>
                              <Button
                                color="error"
                                variant="outlined"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDelete(product.id)}
                                disabled={isProcessing}
                              >
                                {isProcessing ? "Se șterge..." : "Șterge"}
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VinereaExclusivaManager;
